import { FC, useMemo, useState } from "react";
import { Box, Grid, LinearProgress, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useUserStore } from "../../../../../stores/user.store";
import useEnrollmentSummaryReport from "../../../../../hooks/useEnrollmentSummaryReport";
import BasicKPIComponent from "../../../../Dashboard/BasicKPIComponent";
import DataTable from "../../../../../components/DataTable/DataTable";
import { DataTableCell } from "../../../../../components/DataTable/dataTable.interface";
import ReportFiltersBar, {
	ReportFilters,
} from "../../components/ReportFiltersBar";
import StatusChip from "./StatusChip";
import ByOrgUnitChart from "./ByOrgUnitChart";

interface Filters extends ReportFilters {
	pageNumber: number;
	pageSize: number;
}

const HEAD_CELLS = [
	{
		id: "enrollmentId",
		label: "Enrollment ID",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "participantName",
		label: "Participant",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "orgUnit",
		label: "Org unit",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "researcher",
		label: "Researcher",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "startDate",
		label: "Start date",
		isSortable: false,
		disablePadding: false,
	},
	{ id: "day", label: "Day", isSortable: false, disablePadding: false },
	{
		id: "status",
		label: "Status",
		isSortable: false,
		disablePadding: false,
	},
	{
		id: "completionPct",
		label: "Completion %",
		isSortable: false,
		disablePadding: false,
		align: "right" as const,
	},
];

interface EnrollmentSummaryTabProps {
	programId: string;
}

const EnrollmentSummaryTab: FC<EnrollmentSummaryTabProps> = ({ programId }) => {
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";

	const [filters, setFilters] = useState<Filters>({
		orgUnitId: "",
		researcherId: "",
		startDate: null,
		endDate: null,
		pageNumber: 0,
		pageSize: 10,
	});

	const { data, isLoading, isFetching } = useEnrollmentSummaryReport({
		accountId,
		programId,
		orgUnitId: filters.orgUnitId || undefined,
		researcherId: filters.researcherId || undefined,
		startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
		endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
		pageNumber: filters.pageNumber,
		pageSize: filters.pageSize,
	});

	const rows = useMemo<DataTableCell[]>(() => {
		return (data?.enrollments ?? []).map((e, idx) => ({
			id: idx,
			enrollmentId: e.enrollmentId.slice(-6).toUpperCase(),
			participantName: e.participantName,
			orgUnit: e.orgUnit,
			researcher: e.researcher,
			startDate: e.startDate ? dayjs(e.startDate).format("YYYY-MM-DD") : "—",
			day: `${e.day}/${e.totalDays}`,
			status: { id: e.enrollmentId, node: <StatusChip status={e.status} /> },
			completionPct: `${e.completionPct}%`,
		}));
	}, [data]);

	return (
		<Stack spacing={3}>
			<ReportFiltersBar
				idPrefix="enrollment-summary"
				filters={filters}
				onChange={(next) =>
					setFilters((p) => ({ ...p, ...next, pageNumber: 0 }))
				}
				availableOrgUnits={data?.availableOrgUnits ?? []}
				availableResearchers={data?.availableResearchers ?? []}
			/>

			{(isLoading || isFetching) && <LinearProgress />}

			{/* KPI cards */}
			<Grid container spacing={2}>
				<BasicKPIComponent
					title="Total enrolled"
					content={data?.kpis.totalEnrolled ?? 0}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Active"
					content={data?.kpis.active ?? 0}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Completed"
					content={data?.kpis.completed ?? 0}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Disenrolled"
					content={data?.kpis.disenrolled ?? 0}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Avg completion (days)"
					content={data?.kpis.avgCompletionDays ?? 0}
					footer={{ hidden: true }}
				/>
			</Grid>

			{/* Stacked bar chart */}
			<ByOrgUnitChart data={data?.byOrgUnit ?? []} isLoading={isLoading} />

			{/* Enrollment list */}
			<Box>
				<DataTable
					data={rows}
					rowsPerPage={filters.pageSize}
					order="desc"
					orderBy="startDate"
					page={filters.pageNumber}
					headCells={HEAD_CELLS}
					total={data?.pagination.total ?? 0}
					isLoading={isLoading || isFetching}
					messageEmptyData="No enrollments match the selected filters"
					onChangePage={(_event, newPage) => {
						setFilters((p) => ({ ...p, pageNumber: newPage }));
					}}
					onChangeSize={(event) => {
						setFilters((p) => ({
							...p,
							pageNumber: 0,
							pageSize: Number(event.target.value),
						}));
					}}
				/>
			</Box>
		</Stack>
	);
};

export default EnrollmentSummaryTab;
